import { type DlnSource } from '~/data/delivery/dlnSource';

import { i18n } from '~/utils';

export const DELIVERY_NOTES_COLLECTION_ENTITY_KEY = 'deliveryNotes';

/**
 * Filters that are not selectable from the list of filters, but use dedicated UI components.
 */
export const SPECIAL_DELIVERY_NOTES_FILTERS = [
  'custom_field_global_level',
  'custom_field_item_level',
  'dln_created_on',
  'dln_date',
  'dln_modified_on',
  'search',
] as const;

/**
 * Filters that are not selectable from the list of filters, because they don't work with the ID of their respective  entity, but with its name.
 */
export const IGNORED_DELIVERY_NOTES_FILTERS = [
  'authorized_accounting_reference_id',
  'authorized_entity_type',
  'authorized_site_id',
  'billed_state',
  'buyer_name',
  'category',
  'confirmed_accounting_reference_id',
  'confirmed_site_id',
  'issuer_name',
  'ou_id',
  'recipient_name',
  'search',
  'seller_name',
  'supplier_name',
] as const;

export const FILTER_AGGREGATOR_I18N_MAP = () => ({
  and: i18n.t(($) => $.filterAggr.and, { ns: 'filters' }),
  or: i18n.t(($) => $.filterAggr.or, { ns: 'filters' }),
});

export const FILTER_OPERATOR_NUMERIC_I18N_MAP = () => ({
  eq: i18n.t(($) => $.operator.eq, { ns: 'filters' }),
  ge: i18n.t(($) => $.operator.ge, { ns: 'filters' }),
  gt: i18n.t(($) => $.operator.gt, { ns: 'filters' }),
  le: i18n.t(($) => $.operator.le, { ns: 'filters' }),
  lt: i18n.t(($) => $.operator.lt, { ns: 'filters' }),
  ne: i18n.t(($) => $.operator.ne, { ns: 'filters' }),
});

export const FILTER_OPERATOR_STRING_I18N_MAP = () => ({
  contains: i18n.t(($) => $.operator.contains, { ns: 'filters' }),
  endswith: i18n.t(($) => $.operator.endswith, { ns: 'filters' }),
  eq: i18n.t(($) => $.operator.eq, { ns: 'filters' }),
  ne: i18n.t(($) => $.operator.ne, { ns: 'filters' }),
  not_contains: i18n.t(($) => $.operator.not_contains, { ns: 'filters' }),
  not_endswith: i18n.t(($) => $.operator.not_endswith, { ns: 'filters' }),
  not_startswith: i18n.t(($) => $.operator.not_startswith, { ns: 'filters' }),
  startswith: i18n.t(($) => $.operator.startswith, { ns: 'filters' }),
});

export const FILTER_OPERATOR_UUID_I18N_MAP = () => ({
  eq: i18n.t(($) => $.operator.eq, { ns: 'filters' }),
});

export const FILTER_OPERATOR_BOOLEAN_I18N_MAP = () => ({
  eq: i18n.t(($) => $.operator.eq, { ns: 'filters' }),
  ne: i18n.t(($) => $.operator.ne, { ns: 'filters' }),
});

export const FILTER_OPERATOR_ARRAY_I18N_MAP = () => ({
  in: i18n.t(($) => $.operator.in, { ns: 'filters' }),
});

export const FILTER_OPERATOR_I18N_MAP = () => ({
  ...FILTER_OPERATOR_NUMERIC_I18N_MAP(),
  ...FILTER_OPERATOR_STRING_I18N_MAP(),
  ...FILTER_OPERATOR_BOOLEAN_I18N_MAP(),
  ...FILTER_OPERATOR_UUID_I18N_MAP(),
  ...FILTER_OPERATOR_ARRAY_I18N_MAP(),
});

export const FILTER_NAME_I18N_MAP = () => ({
  article_name: i18n.t(($) => $.filter.article_name, { ns: 'deliveryNotes' }),
  article_number: i18n.t(($) => $.filter.article_number, {
    ns: 'deliveryNotes',
  }),
  authorized_accounting_reference_id: i18n.t(
    ($) => $.filter.authorized_accounting_reference_id,
    { ns: 'deliveryNotes' },
  ),
  authorized_entity_type: i18n.t(($) => $.filter.authorized_entity_type, {
    ns: 'deliveryNotes',
  }),
  authorized_site_id: i18n.t(($) => $.filter.authorized_site_id, {
    ns: 'deliveryNotes',
  }),
  billed_state: i18n.t(($) => $.filter.billed_state, { ns: 'deliveryNotes' }),
  buyer_id: i18n.t(($) => $.filter.buyer_id, { ns: 'deliveryNotes' }),
  buyer_name: i18n.t(($) => $.filter.buyer_name, { ns: 'deliveryNotes' }),
  category: i18n.t(($) => $.filter.category, { ns: 'deliveryNotes' }),
  confirmed_accounting_reference_id: i18n.t(
    ($) => $.filter.confirmed_accounting_reference_id,
    { ns: 'deliveryNotes' },
  ),
  confirmed_accounting_reference_name: i18n.t(
    ($) => $.filter.confirmed_accounting_reference_name,
    { ns: 'deliveryNotes' },
  ),
  confirmed_site_id: i18n.t(($) => $.filter.confirmed_site_id, {
    ns: 'deliveryNotes',
  }),
  confirmed_site_name: i18n.t(($) => $.filter.confirmed_site_name, {
    ns: 'deliveryNotes',
  }),
  custom_field_global_level: i18n.t(($) => $.filter.custom_field_global_level, {
    ns: 'deliveryNotes',
  }),
  custom_field_item_level: i18n.t(($) => $.filter.custom_field_item_level, {
    ns: 'deliveryNotes',
  }),
  customer_number: i18n.t(($) => $.filter.customer_number, {
    ns: 'deliveryNotes',
  }),
  dln_accept_state: i18n.t(($) => $.filter.dln_accept_state, {
    ns: 'deliveryNotes',
  }),
  dln_created_on: i18n.t(($) => $.filter.dln_created_on, {
    ns: 'deliveryNotes',
  }),
  dln_date: i18n.t(($) => $.filter.dln_date, { ns: 'deliveryNotes' }),
  dln_modified_on: i18n.t(($) => $.filter.dln_modified_on, {
    ns: 'deliveryNotes',
  }),
  dln_nr: i18n.t(($) => $.filter.dln_nr, { ns: 'deliveryNotes' }),
  is_mapped: i18n.t(($) => $.filter.is_mapped, { ns: 'deliveryNotes' }),
  is_reparsed: i18n.t(($) => $.filter.is_reparsed, { ns: 'deliveryNotes' }),
  issuer_id: i18n.t(($) => $.filter.issuer_id, { ns: 'deliveryNotes' }),
  issuer_name: i18n.t(($) => $.filter.issuer_name, { ns: 'deliveryNotes' }),
  license_plate_number: i18n.t(($) => $.filter.license_plate_number, {
    ns: 'deliveryNotes',
  }),
  loading_location: i18n.t(($) => $.filter.loading_location, {
    ns: 'deliveryNotes',
  }),
  model_id: i18n.t(($) => $.filter.model_id, { ns: 'deliveryNotes' }),
  ou_id: i18n.t(($) => $.filter.ou_id, { ns: 'deliveryNotes' }),
  ou_name: i18n.t(($) => $.filter.ou_name, { ns: 'deliveryNotes' }),
  process_state: i18n.t(($) => $.filter.process_state, { ns: 'deliveryNotes' }),
  recipient_id: i18n.t(($) => $.filter.recipient_id, { ns: 'deliveryNotes' }),
  recipient_name: i18n.t(($) => $.filter.recipient_name, {
    ns: 'deliveryNotes',
  }),
  search: i18n.t(($) => $.filter.search, { ns: 'deliveryNotes' }),
  seller_id: i18n.t(($) => $.filter.seller_id, { ns: 'deliveryNotes' }),
  seller_name: i18n.t(($) => $.filter.seller_name, { ns: 'deliveryNotes' }),
  source: i18n.t(($) => $.filter.source, { ns: 'deliveryNotes' }),
  supplier_assigned_site_name: i18n.t(
    ($) => $.filter.supplier_assigned_site_name,
    {
      ns: 'deliveryNotes',
    },
  ),
  supplier_id: i18n.t(($) => $.filter.supplier_id, { ns: 'deliveryNotes' }),
  supplier_name: i18n.t(($) => $.filter.supplier_name, { ns: 'deliveryNotes' }),
});

export const FILTER_NAME_PROPERTIES_MAP = () => ({
  article_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().article_name,
  },
  article_number: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().article_number,
  },
  authorized_accounting_reference_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().authorized_accounting_reference_id,
  },
  authorized_entity_type: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().authorized_entity_type,
  },
  authorized_site_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().authorized_site_id,
  },
  billed_state: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().billed_state,
  },
  buyer_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().buyer_id,
  },
  buyer_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().buyer_name,
  },
  category: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().category,
  },
  confirmed_accounting_reference_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().confirmed_accounting_reference_id,
  },
  confirmed_accounting_reference_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().confirmed_accounting_reference_name,
  },
  confirmed_site_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().confirmed_site_id,
  },
  confirmed_site_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().confirmed_site_name,
  },
  custom_field_global_level: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().custom_field_global_level,
  },
  custom_field_item_level: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().custom_field_item_level,
  },
  customer_number: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().customer_number,
  },
  dln_accept_state: {
    datatype: 'string',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().dln_accept_state,
  },
  dln_created_on: {
    datatype: 'date',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().dln_created_on,
  },
  dln_date: {
    datatype: 'date',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().dln_date,
  },
  dln_modified_on: {
    datatype: 'date',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().dln_modified_on,
  },
  dln_nr: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().dln_nr,
  },
  is_mapped: {
    datatype: 'boolean',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().is_mapped,
  },
  is_reparsed: {
    datatype: 'boolean',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().is_reparsed,
  },
  issuer_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().issuer_id,
  },
  issuer_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().issuer_name,
  },
  license_plate_number: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().license_plate_number,
  },
  loading_location: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().loading_location,
  },
  model_id: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().model_id,
  },
  ou_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().ou_id,
  },
  ou_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().ou_name,
  },
  process_state: {
    datatype: 'string',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().process_state,
  },
  recipient_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().recipient_id,
  },
  recipient_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().recipient_name,
  },
  search: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().search,
  },
  seller_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().seller_id,
  },
  seller_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().seller_name,
  },
  source: {
    datatype: 'string',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().source,
  },
  supplier_assigned_site_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().supplier_assigned_site_name,
  },
  supplier_id: {
    datatype: 'uuid',
    isArray: true,
    label: FILTER_NAME_I18N_MAP().supplier_id,
  },
  supplier_name: {
    datatype: 'string',
    isArray: false,
    label: FILTER_NAME_I18N_MAP().supplier_name,
  },
});

export const ACCEPT_STATE_I18N_MAP = () => ({
  accepted: i18n.t(($) => $.acceptState.accepted, { ns: 'deliveryNotes' }),
  open: i18n.t(($) => $.acceptState.open, { ns: 'deliveryNotes' }),
  rejected: i18n.t(($) => $.acceptState.rejected, { ns: 'deliveryNotes' }),
});

/**
 * Accept state of a delivery note as stored by the backend. The backend enum
 * is exactly `accepted | rejected | open`; the `dln_accept_state` filter
 * rejects any other value, so the label map above is limited to this set.
 */
export type AcceptState = keyof ReturnType<typeof ACCEPT_STATE_I18N_MAP>;

/**
 * Accept state as derived on the frontend from the line items for the detail
 * page. `partiallyAccepted` has no backend counterpart and must never be sent
 * as a filter value.
 */
export type AcceptStateDeliveryNote = AcceptState | 'partiallyAccepted';

export const PROCESS_STATE_I18N_MAP = () => ({
  arrived: i18n.t(($) => $.processState.arrived, { ns: 'deliveryNotes' }),
  cancelled: i18n.t(($) => $.processState.cancelled, { ns: 'deliveryNotes' }),
  delivered: i18n.t(($) => $.processState.delivered, { ns: 'deliveryNotes' }),
  imported: i18n.t(($) => $.processState.imported, { ns: 'deliveryNotes' }),
  inTransport: i18n.t(($) => $.processState.inTransport, {
    ns: 'deliveryNotes',
  }),
  readyForOutput: i18n.t(($) => $.processState.readyForOutput, {
    ns: 'deliveryNotes',
  }),
  settled: i18n.t(($) => $.processState.settled, { ns: 'deliveryNotes' }),
});

export type ProcessState = keyof ReturnType<typeof PROCESS_STATE_I18N_MAP>;

export const DELIVERY_NOTE_SOURCE_I18N_MAP = (): Record<DlnSource, string> => ({
  ai_output: i18n.t(($) => $.source.ai_output, { ns: 'deliveryNotes' }),
  api_integration: i18n.t(($) => $.source.api_integration, {
    ns: 'deliveryNotes',
  }),
  api_poller: i18n.t(($) => $.source.api_poller, { ns: 'deliveryNotes' }),
  demo_dln: i18n.t(($) => $.source.demo_dln, { ns: 'deliveryNotes' }),
  email: i18n.t(($) => $.source.email, { ns: 'deliveryNotes' }),
  email_scan: i18n.t(($) => $.source.email_scan, { ns: 'deliveryNotes' }),
  ftp: i18n.t(($) => $.source.ftp, { ns: 'deliveryNotes' }),
  legacy: i18n.t(($) => $.source.legacy, { ns: 'deliveryNotes' }),
  mobile_ocr: i18n.t(($) => $.source.mobile_ocr, {
    ns: 'deliveryNotes',
  }),
  mobile_ocr_fast: i18n.t(($) => $.source.mobile_ocr_fast, {
    ns: 'deliveryNotes',
  }),
  mobile_ocr_validated: i18n.t(($) => $.source.mobile_ocr_validated, {
    ns: 'deliveryNotes',
  }),
  mobile_qr: i18n.t(($) => $.source.mobile_qr, { ns: 'deliveryNotes' }),
  reparsing: i18n.t(($) => $.source.reparsing, { ns: 'deliveryNotes' }),
  unknown: i18n.t(($) => $.source.unknown, { ns: 'deliveryNotes' }),
  web_upload: i18n.t(($) => $.source.web_upload, { ns: 'deliveryNotes' }),
});

export const DELIVERY_NOTE_ORDER_BY_I18N_MAP = () => ({
  created_on: i18n.t(($) => $.orderBy.created_on, { ns: 'deliveryNotes' }),
  dln_date: i18n.t(($) => $.orderBy.dln_date, { ns: 'deliveryNotes' }),
  modified_on: i18n.t(($) => $.orderBy.modified_on, { ns: 'deliveryNotes' }),
  supplier_assigned_site_name: i18n.t(
    ($) => $.orderBy.supplier_assigned_site_name,
    {
      ns: 'deliveryNotes',
    },
  ),
  supplier_name: i18n.t(($) => $.orderBy.supplier_name, {
    ns: 'deliveryNotes',
  }),
});
